
import React, { useContext, useEffect, useRef, useState } from "react";
import logo from "../assets/images/inqmise-wordmark.png";
import drawerLogo from "../assets/images/inqmise-wordmark.png";
import { LuMenu, LuX } from "react-icons/lu";
import { FaChevronDown, FaArrowRight } from "react-icons/fa6";
import { LuCode, LuMonitor, LuSettings2, LuLaptop, LuHeadset, LuDatabase } from "react-icons/lu";
import { Link, useLocation } from "react-router";
import { lazy, Suspense } from "react";

// antd ships only this Drawer to the public site, and pulling it in
// eagerly dragged the whole antd runtime — theme engine, cssinjs, rc-*
// helpers — into the entry chunk that every visitor downloads. It is now
// a separate chunk, fetched the first time the mobile menu is opened and
// prefetched on pointer-down so the open still feels immediate.
// Import the drawer module directly rather than the "antd" barrel: a dynamic
// import of the barrel cannot be tree-shaken and pulled in the entire
// library (~1.5 MB) as one chunk.
const Drawer = lazy(() => import("antd/es/drawer"));
const preloadDrawer = () => import("antd/es/drawer");
import { GlobalStateContext } from "../controller/GlobalState";
import { aiServicePages, productPages, serviceCategories, blogPosts } from "../controller/global";
import { getPosts } from "../controller/api";

const featuredCaseStudy = blogPosts.find((p) => p.slug === "ai-style-recommendation");

// Client Testimonials, Awards, Compliances, and Security are intentionally
// left out — Testimonials only has fake quotes attributed to real people
// (Tim Cook, Satya Nadella) in an unused component, and the other three are
// factual claims (certifications, awards, CSR activity) still pending real
// details from the client. See conversation history for the flag.
const aboutMenuItems = [
  { title: "About InQmise", path: "/about", description: "Our story, expertise and approach." },
  { title: "Our Core Team", path: "/our-core-team", description: "Meet the people behind your next build." },
  { title: "How We Work and Function", path: "/how-we-work-and-function", description: "From discovery to delivery and support." },
  { title: "Start Your Career with Us", path: "/career", description: "Explore opportunities to grow with us." },
  { title: "FAQ", path: "/faq", description: "Answers to your project questions." },
];

// Case Studies isn't repeated here since it's already a top-level nav item
// and shown just below as the recommended list.
const resourceMenuItems = [
  { title: "Blog", path: "/blog" },
];

// Lucide's line-style icons (rather than solid glyphs) match the reference's
// thin outline icon look.
const CATEGORY_ICONS = {
  "Product Development & Engineering": LuMonitor,
  "Digital Transformation": LuSettings2,
  "Consulting Services": LuLaptop,
  "IT Managed & Outsourcing": LuHeadset,
  "Data Services": LuDatabase,
};

const Navbar = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Gates the lazily-loaded Drawer chunk: false until the menu is first opened.
  const [drawerMounted, setDrawerMounted] = useState(false);
  const [selected, setSelected] = useState("");
  const [scrollUp, setScrollUp] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [servicesPanel, setServicesPanel] = useState("services"); // "services" | "case-studies" — which content the middle column shows
  const [companyServicesOpen, setCompanyServicesOpen] = useState(false);
  const [desktopServiceCategory, setDesktopServiceCategory] = useState(0);
  const activeServiceCategory = serviceCategories[desktopServiceCategory];
  const [expandedServiceItem, setExpandedServiceItem] = useState(null);
  const facetHoverTimer = useRef(null);
  const openFacetPopup = (path) => {
    clearTimeout(facetHoverTimer.current);
    setExpandedServiceItem(path);
  };
  const closeFacetPopup = () => {
    clearTimeout(facetHoverTimer.current);
    facetHoverTimer.current = setTimeout(() => setExpandedServiceItem(null), 180);
  };
  useEffect(() => () => clearTimeout(facetHoverTimer.current), []);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  // Which left-column item is hovered, so the middle column swaps with it.
  // Which single accordion section is open in the mobile drawer — one value
  // rather than four independent booleans, so opening one always closes any
  // other that was open (previously each toggled independently, so e.g. "AI
  // Solutions" and "Resources" could both stay expanded at once, stacking
  // into one long, disorganized list).
  const [mobileOpenSection, setMobileOpenSection] = useState(null); // "ai" | "services" | "about" | "resources" | null
  const mobileServicesOpen = mobileOpenSection === "ai";
  const mobileCompanyServicesOpen = mobileOpenSection === "services";
  const mobileAboutOpen = mobileOpenSection === "about";
  const mobileResourcesOpen = mobileOpenSection === "resources";
  const toggleMobileSection = (section) => setMobileOpenSection((prev) => (prev === section ? null : section));
  // Which single Services category is expanded within the mobile drawer's
  // "Services" accordion — categories have no page of their own to link to
  // (only the items inside them do), so the header's real job is to
  // expand/collapse its own item list, same idea as the outer sections.
  const [mobileServiceCategory, setMobileServiceCategory] = useState(null);
  const toggleMobileServiceCategory = (title) => setMobileServiceCategory((prev) => (prev === title ? null : title));
  // Mirrors the desktop mega menu's "+"/"−" facet expander (expandedServiceItem
  // above) for a service item's sub-links — same idea, just an inline
  // expanding list here instead of a hover flyout, since there's no hover on
  // a touch drawer.
  const [mobileExpandedFacetItem, setMobileExpandedFacetItem] = useState(null);
  const toggleMobileFacetItem = (path) => setMobileExpandedFacetItem((prev) => (prev === path ? null : path));
  const [blogList, setBlogList] = useState([]);
  const { setIsOpen } = useContext(GlobalStateContext);
  const closeTimeoutRef = useRef(null);
  const companyServicesCloseTimeoutRef = useRef(null);
  const aboutCloseTimeoutRef = useRef(null);
  const resourcesCloseTimeoutRef = useRef(null);
  const productCloseTimeoutRef = useRef(null);

  // The mega menu is `position: fixed`, so the cursor briefly crosses the
  // navbar's own background (not a descendant of the trigger <li>) on its way
  // down to it. A plain onMouseLeave on the <li> fires during that crossing
  // and unmounts the menu before the pointer arrives — so closing is delayed
  // and cancellable from either the trigger or the menu itself.
  const openServicesMenu = () => {
    clearTimeout(closeTimeoutRef.current);
    // Only one mega menu should ever be open at a time — otherwise moving the
    // mouse quickly from this one to the other briefly overlaps both, since
    // this one's close is delayed but the other's open is immediate.
    clearTimeout(companyServicesCloseTimeoutRef.current);
    clearTimeout(aboutCloseTimeoutRef.current);
    clearTimeout(resourcesCloseTimeoutRef.current);
    setCompanyServicesOpen(false);
    setAboutOpen(false);
    setResourcesOpen(false);
    setServicesOpen(true);
  };
  const scheduleCloseServicesMenu = () => {
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => {
      setServicesOpen(false);
      setServicesPanel("services");
    }, 200);
  };

  // Same delayed-close pattern, for the separate "Services" mega menu.
  const openCompanyServicesMenu = () => {
    clearTimeout(companyServicesCloseTimeoutRef.current);
    clearTimeout(closeTimeoutRef.current);
    clearTimeout(aboutCloseTimeoutRef.current);
    clearTimeout(resourcesCloseTimeoutRef.current);
    setServicesOpen(false);
    setAboutOpen(false);
    setResourcesOpen(false);
    setCompanyServicesOpen(true);
  };
  const scheduleCloseCompanyServicesMenu = () => {
    clearTimeout(companyServicesCloseTimeoutRef.current);
    companyServicesCloseTimeoutRef.current = setTimeout(() => {
      setCompanyServicesOpen(false);
      setExpandedServiceItem(null);
    }, 200);
  };

  // Same delayed-close pattern, for the "About" mega menu.
  const openAboutMenu = () => {
    clearTimeout(aboutCloseTimeoutRef.current);
    clearTimeout(closeTimeoutRef.current);
    clearTimeout(companyServicesCloseTimeoutRef.current);
    clearTimeout(resourcesCloseTimeoutRef.current);
    setServicesOpen(false);
    setCompanyServicesOpen(false);
    setResourcesOpen(false);
    setAboutOpen(true);
  };
  const scheduleCloseAboutMenu = () => {
    clearTimeout(aboutCloseTimeoutRef.current);
    aboutCloseTimeoutRef.current = setTimeout(() => setAboutOpen(false), 200);
  };

  // Same delayed-close pattern, for the small "Product" dropdown.
  const openProductMenu = () => {
    clearTimeout(productCloseTimeoutRef.current);
    clearTimeout(closeTimeoutRef.current);
    clearTimeout(companyServicesCloseTimeoutRef.current);
    clearTimeout(aboutCloseTimeoutRef.current);
    clearTimeout(resourcesCloseTimeoutRef.current);
    setServicesOpen(false);
    setCompanyServicesOpen(false);
    setAboutOpen(false);
    setResourcesOpen(false);
    setProductOpen(true);
  };
  const scheduleCloseProductMenu = () => {
    clearTimeout(productCloseTimeoutRef.current);
    productCloseTimeoutRef.current = setTimeout(() => setProductOpen(false), 200);
  };

  // Same delayed-close pattern, for the "Resources" mega menu.
  const openResourcesMenu = () => {
    clearTimeout(resourcesCloseTimeoutRef.current);
    clearTimeout(closeTimeoutRef.current);
    clearTimeout(companyServicesCloseTimeoutRef.current);
    clearTimeout(aboutCloseTimeoutRef.current);
    setServicesOpen(false);
    setCompanyServicesOpen(false);
    setAboutOpen(false);
    setResourcesOpen(true);
  };
  const scheduleCloseResourcesMenu = () => {
    clearTimeout(resourcesCloseTimeoutRef.current);
    resourcesCloseTimeoutRef.current = setTimeout(() => setResourcesOpen(false), 200);
  };

  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);
  useEffect(() => () => clearTimeout(companyServicesCloseTimeoutRef.current), []);
  useEffect(() => () => clearTimeout(aboutCloseTimeoutRef.current), []);
  useEffect(() => () => clearTimeout(resourcesCloseTimeoutRef.current), []);

  const burgerRef = useRef(null);

  const handleOpen = () => {
    setDrawerMounted(true);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setMobileOpenSection(null);
    setMobileServiceCategory(null);
    setMobileExpandedFacetItem(null);
  };

  // <Link> performs the navigation itself, so these run only the side effects
  // the old click handlers did: close any open menu and update the active
  // highlight. Kept as separate functions because the mobile and desktop
  // menus close different pieces of state.
  const afterNavigate = (val) => {
    setSelected(val);
    setServicesOpen(false);
    setCompanyServicesOpen(false);
    setAboutOpen(false);
    setResourcesOpen(false);
    handleClose();
  };

  const afterNavigateToPath = (path) => {
    setSelected(path.split("#")[0].split("/")[1] || "");
    setServicesOpen(false);
    setCompanyServicesOpen(false);
    setAboutOpen(false);
    setResourcesOpen(false);
    setExpandedServiceItem(null);
    handleClose();
  };

  const isAiMenuActive = aiServicePages.some((s) => s.path === `/${selected}`);

  useEffect(() => {
    setSelected(location.pathname.split("/")[1] || "");
  }, [location]);

  // Fetched once for the Resources mega menu's Trending/Latest columns —
  // getPosts() already returns published posts newest-first.
  useEffect(() => {
    getPosts().then(setBlogList).catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 20);
      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setScrollUp(false);
      } else {
        setScrollUp(true);
      }
      setLastScrollY(currentScrollY <= 0 ? 0 : currentScrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const navItems = [
    { key: "about", label: "About" },
    // { key: "technologies", label: "Technologies" },
    { key: "ai", label: "AI Solutions" },
    { key: "product", label: "Products" },
    { key: "services", label: "Services" },
    { key: "case-studies", label: "Case Studies" },
    // { key: "resources", label: "Resources" },
  ];

  return (
    <header
      className={`navbar w-full h-20 fixed z-50 transition-all duration-500 ease-in-out bg-[var(--pine)]
        ${scrollUp ? "translate-y-0" : "-translate-y-full"}
        ${scrolled ? "shadow-[0_4px_24px_rgba(0,0,0,0.25)]" : ""}`}
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-[var(--leaf)]" />

      <div className="h-20 flex justify-between items-center px-6 sm:px-10 lg:px-16">
        {/* Logo */}
        <Link to="/"
          className="logo flex items-center cursor-pointer group"
          onClick={() => afterNavigate("")}
        >
          <img
            src={logo}
            alt="InQmise Technologies Logo"
            loading="eager"
            className="header-brand-logo h-6 sm:h-7 md:h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden xl:flex items-center gap-8">
          <ul className="flex items-center gap-1 list-none m-0 p-0">
            {navItems.map((item) => {
              if (item.key === "ai") {
                return (
                  <li
                    key={item.key}
                    className="relative h-20 flex items-center"
                    onMouseEnter={openServicesMenu}
                    onMouseLeave={scheduleCloseServicesMenu}
                  >
                    <span
                      className={`relative flex items-center gap-1 pl-4 pr-2 py-2 text-[15px] font-medium rounded-lg transition-all duration-300
                        ${isAiMenuActive
                          ? "text-[var(--leaf)]"
                          : "text-white/85"
                        }`}
                    >
                      <Link to="/ai-solutions"
                        onClick={() => afterNavigateToPath("/ai-solutions")}
                        className="inline-block cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        {item.label}
                      </Link>
                      <button
                        onClick={() => setServicesOpen((prev) => !prev)}
                        aria-expanded={servicesOpen}
                        aria-label={servicesOpen ? "Collapse AI Solutions menu" : "Expand AI Solutions menu"}
                        className="p-1 cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        <FaChevronDown className={`text-[10px] transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isAiMenuActive && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-[var(--leaf)] rounded-full" />
                      )}
                    </span>

                    {/* Mega menu */}
                    {servicesOpen && (
                      <div
                        className="nav-mega-panel fixed left-0 right-0 top-20 z-50 h-[400px] overflow-hidden border-t border-[var(--line)] bg-[var(--paper)] shadow-[0_30px_60px_rgba(0,0,0,0.18)] animate-fade-slide"
                        onMouseEnter={openServicesMenu}
                        onMouseLeave={scheduleCloseServicesMenu}
                      >
                        <div className="h-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr_300px] gap-10">
                          {/* Column 1 — CTA + promo */}
                          <div className="h-full overflow-y-auto flex flex-col gap-3">
                            <Link to="/ai-solutions"
                              onMouseEnter={() => setServicesPanel("services")}
                              onClick={() => afterNavigateToPath("/ai-solutions")}
                              className={`group flex items-center justify-between gap-3 font-semibold text-sm rounded-lg px-4 py-3.5 transition-colors duration-200 cursor-pointer
                                ${servicesPanel === "services" ? "bg-[var(--mist)] text-[var(--leaf)]" : "text-[var(--ink)] hover:bg-[var(--mist)] hover:text-[var(--leaf)]"}`}
                            >
                              Explore AI Services
                              <FaArrowRight
                                className={`text-xs flex-shrink-0 transition-all duration-200 ${servicesPanel === "services" ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0"}`}
                                aria-hidden="true"
                              />
                            </Link>
                            <Link to="/case-studies"
                              onMouseEnter={() => setServicesPanel("case-studies")}
                              onClick={() => afterNavigateToPath("/case-studies")}
                              className={`group flex items-center justify-between gap-3 text-left text-sm font-medium rounded-lg px-4 py-3 transition-colors duration-200 cursor-pointer
                                ${servicesPanel === "case-studies" ? "bg-[var(--mist)] text-[var(--leaf)]" : "text-[var(--ink)] hover:bg-[var(--mist)] hover:text-[var(--leaf)]"}`}
                            >
                              Case Studies
                              <FaArrowRight
                                className={`text-xs flex-shrink-0 transition-all duration-200 ${servicesPanel === "case-studies" ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0"}`}
                                aria-hidden="true"
                              />
                            </Link>

                            <div className="mt-auto rounded-xl p-5 bg-[var(--sun)] relative overflow-hidden">
                              <p className="text-[11px] font-bold uppercase tracking-wide text-[var(--ink)]/60 mb-2">InQmise</p>
                              <h4 className="text-lg font-bold text-[var(--ink)] mb-1.5 leading-snug" style={{ fontFamily: "Syne, sans-serif" }}>
                                Have a project in mind?
                              </h4>
                              <p className="text-xs text-[var(--ink)]/70 mb-3 leading-relaxed">
                                Let's scope it together, no obligation.
                              </p>
                              <button
                                onClick={() => { setIsOpen(true); setServicesOpen(false); }}
                                className="ea-text-link"
                              >
                                Start a Conversation ↗
                              </button>
                            </div>
                          </div>

                          {/* Column 2 — swaps content based on which item in column 1 is hovered */}
                          <div className="h-full overflow-y-auto flex flex-col lg:border-l lg:border-r border-[var(--line)] lg:px-10">
                            {servicesPanel === "case-studies" ? (
                              <>
                                <div className="flex items-center justify-between mb-3">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">Recommended Case Studies</p>
                                  <Link to="/case-studies"
                                    onClick={() => afterNavigateToPath("/case-studies")}
                                    className="inline-block text-xs font-semibold text-[var(--leaf)] hover:underline underline-offset-2 cursor-pointer flex-shrink-0"
                                  >
                                    View All Case Studies →
                                  </Link>
                                </div>
                                {blogPosts.map((post) => (
                                  <Link to={`/case-studies/${post.slug}`}
                                    key={post.slug}
                                    onClick={() => afterNavigateToPath(`/case-studies/${post.slug}`)}
                                    className="flex items-center gap-3 text-left py-2.5 border-b border-[var(--line)] last:border-0 cursor-pointer group"
                                  >
                                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-[var(--mist)] flex-shrink-0">
                                      <img
                                        src={post.image}
                                        alt={post.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                      />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-[10px] font-semibold text-[var(--leaf)] uppercase tracking-wide mb-0.5 truncate">{post.category}</p>
                                      <p className="text-sm text-[var(--ink)] leading-snug line-clamp-2 group-hover:text-[var(--leaf)] transition-colors duration-200">{post.title}</p>
                                    </div>
                                  </Link>
                                ))}
                              </>
                            ) : (
                              <>
                                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)] mb-3">AI Services &amp; Solutions</p>
                                {aiServicePages.map((s) => (
                                  <Link to={s.path}
                                    key={s.path}
                                    onMouseEnter={() => setServicesPanel("services")}
                                    onClick={() => afterNavigateToPath(s.path)}
                                    className="group flex items-center justify-between gap-3 text-left py-3 text-[15px] text-[var(--ink)] hover:text-[var(--leaf)] transition-colors duration-200 border-b border-[var(--line)] last:border-0 cursor-pointer"
                                  >
                                    {s.title}
                                    <FaArrowRight className="text-xs text-[var(--leaf)] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 flex-shrink-0" aria-hidden="true" />
                                  </Link>
                                ))}
                              </>
                            )}
                          </div>

                          {/* Column 3 — featured case study */}
                          {featuredCaseStudy && (
                            <div className="h-full overflow-y-auto">
                              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)] mb-3">Featured Case Study</p>
                              <Link to="/case-studies/ai-style-recommendation"
                                onClick={() => afterNavigateToPath("/case-studies/ai-style-recommendation")}
                                className="block text-left group cursor-pointer w-full"
                              >
                                <div className="rounded-lg overflow-hidden mb-3 aspect-video bg-[var(--mist)]">
                                  <img
                                    src={featuredCaseStudy.image}
                                    alt={featuredCaseStudy.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  />
                                </div>
                                <p className="text-[11px] font-semibold text-[var(--leaf)] uppercase tracking-wide mb-1.5">{featuredCaseStudy.category}</p>
                                <p className="text-sm font-semibold text-[var(--ink)] leading-snug mb-2">{featuredCaseStudy.title}</p>
                                <span className="text-xs font-semibold text-[var(--leaf)] underline underline-offset-2">Read Full Case Study →</span>
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              }

              if (item.key === "services") {
                return (
                  <li
                    key={item.key}
                    className="relative h-20 flex items-center"
                    onMouseEnter={openCompanyServicesMenu}
                    onMouseLeave={scheduleCloseCompanyServicesMenu}
                  >
                    <span
                      className={`relative flex items-center gap-1 pl-4 pr-2 py-2 text-[15px] font-medium rounded-lg transition-all duration-300
                        ${selected === "services"
                          ? "text-[var(--leaf)]"
                          : "text-white/85"
                        }`}
                    >
                      <Link to="/services"
                        onClick={() => afterNavigate("services")}
                        className="inline-block cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        {item.label}
                      </Link>
                      <button
                        onClick={() => setCompanyServicesOpen((prev) => !prev)}
                        aria-expanded={companyServicesOpen}
                        aria-label={companyServicesOpen ? "Collapse Services menu" : "Expand Services menu"}
                        className="p-1 cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        <FaChevronDown className={`text-[10px] transition-transform duration-300 ${companyServicesOpen ? "rotate-180" : ""}`} />
                      </button>
                      {selected === "services" && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-[var(--leaf)] rounded-full" />
                      )}
                    </span>

                    {/* Categories and facets share a fixed-height frame without nested scrolling. */}
                    {companyServicesOpen && (
                      <div
                        className="nav-mega-panel nav-services-panel fixed left-0 right-0 top-20 z-50 flex flex-col border-t border-[var(--line)] bg-[var(--paper)] shadow-[0_30px_60px_rgba(0,0,0,0.18)] animate-fade-slide"
                        onMouseEnter={openCompanyServicesMenu}
                        onMouseLeave={scheduleCloseCompanyServicesMenu}
                      >
                        <div className="nav-services-body">
                          <nav className="nav-service-categories" aria-label="Service categories">
                            <p>Explore our expertise</p>
                            {serviceCategories.map((category, index) => {
                              const CategoryIcon = CATEGORY_ICONS[category.title] || LuCode;
                              return <button key={category.title} type="button" aria-pressed={desktopServiceCategory === index} aria-controls="desktop-service-list"
                                onMouseEnter={() => { setDesktopServiceCategory(index); setExpandedServiceItem(null); }}
                                onFocus={() => { setDesktopServiceCategory(index); setExpandedServiceItem(null); }}
                                onClick={() => { setDesktopServiceCategory(index); setExpandedServiceItem(null); }}>
                                <CategoryIcon aria-hidden="true" /><span>{category.title}</span><FaArrowRight aria-hidden="true" />
                              </button>;
                            })}
                          </nav>
                          <div className="nav-services-content" id="desktop-service-list">
                            <div className="nav-services-heading">
                              <h3>{activeServiceCategory.title}</h3>
                              <Link to="/services" onClick={() => afterNavigateToPath("/services")}>View all services ↗</Link>
                            </div>
                            <div className="nav-service-grid">
                              {activeServiceCategory.items.map((service, index) => {
                                const popupOpen = expandedServiceItem === service.path;
                                const popupId = `desktop-service-facets-${desktopServiceCategory}-${index}`;
                                return (
                                  <div className="nav-service-entry" key={service.path}
                                    onMouseEnter={() => clearTimeout(facetHoverTimer.current)} onMouseLeave={closeFacetPopup}
                                    onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) closeFacetPopup(); }}
                                    onKeyDown={(event) => { if (event.key === "Escape" && popupOpen) { event.stopPropagation(); setExpandedServiceItem(null); event.currentTarget.querySelector("button")?.focus(); } }}>
                                    <Link to={service.path} onClick={() => afterNavigateToPath(service.path)}>{service.title}</Link>
                                    {service.facets?.length ? (
                                      <>
                                        <button type="button" aria-label={`${popupOpen ? "Collapse" : "Expand"} ${service.title} details`} aria-expanded={popupOpen} aria-controls={popupId}
                                          onMouseEnter={() => openFacetPopup(service.path)} onClick={() => { clearTimeout(facetHoverTimer.current); setExpandedServiceItem(popupOpen ? null : service.path); }}>{popupOpen ? "−" : "+"}</button>
                                        {popupOpen && (
                                          <div className="nav-facet-popup" id={popupId} role="region" aria-label={`${service.title} sub-services`} onMouseEnter={() => clearTimeout(facetHoverTimer.current)}>
                                            <h4>{service.title}</h4>
                                            <div>{service.facets.map((facet) => <Link key={`${facet.title}-${facet.path}`} to={facet.path} onClick={() => afterNavigateToPath(facet.path)}>{facet.title}<FaArrowRight aria-hidden="true" /></Link>)}</div>
                                          </div>
                                        )}
                                      </>
                                    ) : <FaArrowRight aria-hidden="true" />}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Persistent consultation CTA */}
                        <div className="flex-shrink-0 border-t border-[var(--line)] bg-[var(--mist)]">
                          <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                            <p className="text-sm text-[var(--ink)] text-center sm:text-left">
                              Didn't find what you're looking for? Let us know your needs, and we'll tailor a solution just for you.
                            </p>
                            <button
                              onClick={() => { setIsOpen(true); setCompanyServicesOpen(false); }}
                              className="ea-button ea-primary flex-shrink-0 whitespace-nowrap"
                            >
                              Schedule Free Consultation
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              }

              if (item.key === "product") {
                const productActive = productPages.some((product) => product.path === `/${selected}`);
                return (
                  <li
                    key={item.key}
                    className="relative h-20 flex items-center"
                    onMouseEnter={openProductMenu}
                    onMouseLeave={scheduleCloseProductMenu}
                  >
                    <span
                      className={`relative flex items-center gap-1 pl-4 pr-2 py-2 text-[15px] font-medium rounded-lg transition-all duration-300
                        ${productActive ? "text-[var(--leaf)]" : "text-white/85"}`}
                    >
                      <Link to={productPages[0].path}
                        onClick={() => afterNavigateToPath(productPages[0].path)}
                        className="inline-block cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        {item.label}
                      </Link>
                      <button
                        onClick={() => setProductOpen((prev) => !prev)}
                        aria-expanded={productOpen}
                        aria-label={productOpen ? "Collapse Products menu" : "Expand Products menu"}
                        className="p-1 cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        <FaChevronDown className={`text-[10px] transition-transform duration-300 ${productOpen ? "rotate-180" : ""}`} />
                      </button>
                      {productActive && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-[var(--leaf)] rounded-full" />
                      )}
                    </span>

                    {/* Mega menu — same three-column shape as AI Solutions */}
                    {productOpen && (
                      <div
                        className="nav-mega-panel fixed left-0 right-0 top-20 z-50 h-[400px] overflow-hidden border-t border-[var(--line)] bg-[var(--paper)] shadow-[0_30px_60px_rgba(0,0,0,0.18)] animate-fade-slide"
                        onMouseEnter={openProductMenu}
                        onMouseLeave={scheduleCloseProductMenu}
                      >
                        <div className="nav-product-layout">
                          <div className="nav-product-brand">
                            <span className="nav-about-label">Products by InQmise</span>
                            <h3>Practical tools.<br />Built for your business.</h3>
                            <p>Explore our products, discover what they do, and find the right fit for your team.</p>
                            <div className="nav-product-brand-note">Designed and developed by the InQmise team.</div>
                            <button onClick={() => { setIsOpen(true); setProductOpen(false); }}>Talk to our team <FaArrowRight aria-hidden="true" /></button>
                          </div>
                          <div className="nav-product-catalog">
                            <h3>Explore our products</h3>
                            <div className="nav-product-cards">
                              {productPages.map((product) => (
                                <Link key={product.path} to={product.path} onClick={() => afterNavigateToPath(product.path)} className="nav-product-card">
                                  <div className={`nav-product-card-logo${product.title === "Kraftigo" ? " nav-product-card-logo-light" : ""}`}>
                                    {product.logo ? <img src={product.logo} alt={product.title} /> : <span>{product.title}</span>}
                                  </div>
                                  <span className="nav-product-card-copy"><strong>{product.title}</strong><small>{product.desc}</small></span>
                                  <FaArrowRight aria-hidden="true" />
                                </Link>
                              ))}
                              {/* Temporary useful links: remove these entries as new products are added. */}
                              {[
                                { title: "Build a custom product", desc: "Explore our software development services.", path: "/custom-software-development", icon: LuCode },
                              ].map((entry) => (
                                <Link key={entry.path} to={entry.path} onClick={() => afterNavigateToPath(entry.path)} className="nav-product-card nav-product-utility">
                                  <span className="nav-product-card-logo">{React.createElement(entry.icon, { "aria-hidden": true })}</span>
                                  <span className="nav-product-card-copy"><strong>{entry.title}</strong><small>{entry.desc}</small></span>
                                  <FaArrowRight aria-hidden="true" />
                                </Link>
                              ))}
                            </div>
                          </div>
                          <div className="nav-product-next">
                            <Link className="nav-product-preview" to="/products/cledge#in-action" onClick={() => afterNavigateToPath("/products/cledge#in-action")}>
                              <span className="nav-about-label">A simpler clinic workflow</span>
                              <h3>From booking<br />to follow-up.</h3>
                              <div className="nav-product-journey"><span>Book & check in</span><span>Consult & prescribe</span><span>Keep care connected</span></div>
                              <strong>See Cledge in action <FaArrowRight aria-hidden="true" /></strong>
                            </Link>
                            <div className="nav-product-contact"><p>Building something of your own?</p><button onClick={() => { setIsOpen(true); setProductOpen(false); }}>Discuss your project <FaArrowRight aria-hidden="true" /></button></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              }

              if (item.key === "about") {
                return (
                  <li
                    key={item.key}
                    className="relative h-20 flex items-center"
                    onMouseEnter={openAboutMenu}
                    onMouseLeave={scheduleCloseAboutMenu}
                  >
                    <span
                      className={`relative flex items-center gap-1 pl-4 pr-2 py-2 text-[15px] font-medium rounded-lg transition-all duration-300
                        ${selected === "about"
                          ? "text-[var(--leaf)]"
                          : "text-white/85"
                        }`}
                    >
                      <Link to="/about"
                        onClick={() => afterNavigateToPath("/about")}
                        className="inline-block cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        {item.label}
                      </Link>
                      <button
                        onClick={() => setAboutOpen((prev) => !prev)}
                        aria-expanded={aboutOpen}
                        aria-label={aboutOpen ? "Collapse About menu" : "Expand About menu"}
                        className="p-1 cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        <FaChevronDown className={`text-[10px] transition-transform duration-300 ${aboutOpen ? "rotate-180" : ""}`} />
                      </button>
                      {selected === "about" && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-[var(--leaf)] rounded-full" />
                      )}
                    </span>

                    {aboutOpen && (
                      <div
                        className="nav-mega-panel fixed left-0 right-0 top-20 z-50 border-t border-[var(--line)] bg-[var(--paper)] shadow-[0_30px_60px_rgba(0,0,0,0.18)] animate-fade-slide"
                        onMouseEnter={openAboutMenu}
                        onMouseLeave={scheduleCloseAboutMenu}
                      >
                        <div className="nav-about-layout">
                          <div className="nav-about-intro">
                            <span className="nav-about-label">Discover InQmise</span>
                            <h3>People, ideas.<br />Better software.</h3>
                            <p>Get to know the team building software, AI and cloud solutions around your business.</p>
                            <Link to="/about" onClick={() => afterNavigateToPath("/about")} className="nav-about-intro-link">Get to know us <FaArrowRight aria-hidden="true" /></Link>
                            <span className="nav-about-location">Delhi NCR · Working worldwide</span>
                          </div>
                          <div className="nav-about-links">
                            {aboutMenuItems.map((aboutItem) => (
                              <Link to={aboutItem.path} key={aboutItem.path} onClick={() => afterNavigateToPath(aboutItem.path)}>
                                <span><strong>{aboutItem.title}</strong><small>{aboutItem.description}</small></span>
                                <FaArrowRight aria-hidden="true" />
                              </Link>
                            ))}
                          </div>
                          {featuredCaseStudy && (
                            <Link to="/case-studies/ai-style-recommendation" onClick={() => afterNavigateToPath("/case-studies/ai-style-recommendation")} className="nav-about-feature">
                              <span className="nav-about-label">Ideas in action</span>
                              <img src={featuredCaseStudy.image} alt={featuredCaseStudy.title} loading="lazy" />
                              <small>{featuredCaseStudy.category}</small>
                              <strong>{featuredCaseStudy.title}</strong>
                              <span className="nav-about-feature-cta">Explore the case study <FaArrowRight aria-hidden="true" /></span>
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              }

              if (item.key === "resources") {
                return (
                  <li
                    key={item.key}
                    className="relative h-20 flex items-center"
                    onMouseEnter={openResourcesMenu}
                    onMouseLeave={scheduleCloseResourcesMenu}
                  >
                    <span
                      className={`relative flex items-center gap-1 pl-4 pr-2 py-2 text-[15px] font-medium rounded-lg transition-all duration-300
                        ${selected === "resources"
                          ? "text-[var(--leaf)]"
                          : "text-white/85"
                        }`}
                    >
                      <Link to="/blog"
                        onClick={() => afterNavigateToPath("/blog")}
                        className="inline-block cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        {item.label}
                      </Link>
                      <button
                        onClick={() => setResourcesOpen((prev) => !prev)}
                        aria-expanded={resourcesOpen}
                        aria-label={resourcesOpen ? "Collapse Resources menu" : "Expand Resources menu"}
                        className="p-1 cursor-pointer hover:text-[var(--leaf)] transition-colors duration-300"
                      >
                        <FaChevronDown className={`text-[10px] transition-transform duration-300 ${resourcesOpen ? "rotate-180" : ""}`} />
                      </button>
                      {selected === "resources" && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-[var(--leaf)] rounded-full" />
                      )}
                    </span>

                    {resourcesOpen && (
                      <div
                        className="nav-mega-panel fixed left-0 right-0 top-20 z-50 border-t border-[var(--line)] bg-[var(--paper)] shadow-[0_30px_60px_rgba(0,0,0,0.18)] animate-fade-slide"
                        onMouseEnter={openResourcesMenu}
                        onMouseLeave={scheduleCloseResourcesMenu}
                      >
                        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-8 grid grid-cols-1 lg:grid-cols-[260px_1fr_300px] gap-10">
                          {/* Column 1 — Resources links */}
                          <div className="flex flex-col">
                            {resourceMenuItems.map((resourceItem) => (
                              <Link to={resourceItem.path}
                                key={resourceItem.path}
                                onClick={() => afterNavigateToPath(resourceItem.path)}
                                className="group flex items-center justify-between gap-3 text-left py-3.5 border-b border-[var(--line)] cursor-pointer"
                              >
                                <span className="text-[15px] text-[var(--ink)] group-hover:text-[var(--leaf)] transition-colors duration-200">
                                  {resourceItem.title}
                                </span>
                                <FaArrowRight className="text-xs text-[var(--leaf)] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 flex-shrink-0" aria-hidden="true" />
                              </Link>
                            ))}
                            <div className="mt-5 rounded-xl p-5 bg-[var(--sun)] relative overflow-hidden">
                              <p className="text-[11px] font-bold uppercase tracking-wide text-[var(--ink)]/60 mb-2">InQmise</p>
                              <h4 className="text-lg font-bold text-[var(--ink)] mb-1.5 leading-snug" style={{ fontFamily: "Syne, sans-serif" }}>
                                New ideas, every week.
                              </h4>
                              <p className="text-xs text-[var(--ink)]/70 mb-3 leading-relaxed">
                                Practical AI &amp; software insights for teams building what's next.
                              </p>
                              <Link to="/blog" onClick={() => afterNavigateToPath("/blog")} className="inline-block ea-text-link">
                                Read the Blog ↗
                              </Link>
                            </div>
                          </div>

                          {/* Column 2 — trending posts (top of the newest-first feed) */}
                          <div className="flex flex-col lg:border-l lg:border-r border-[var(--line)] lg:px-10">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">Trending</p>
                              <Link to="/blog"
                                onClick={() => afterNavigateToPath("/blog")}
                                className="inline-block text-xs font-semibold text-[var(--leaf)] hover:underline underline-offset-2 cursor-pointer flex-shrink-0"
                              >
                                View All →
                              </Link>
                            </div>
                            {blogList.slice(1, 4).map((post) => (
                              <Link to={`/blog/${post.slug}`}
                                key={post.slug}
                                onClick={() => afterNavigateToPath(`/blog/${post.slug}`)}
                                className="flex items-center gap-3 text-left py-2.5 border-b border-[var(--line)] last:border-0 cursor-pointer group"
                              >
                                <div className="w-14 h-14 rounded-lg overflow-hidden bg-[var(--mist)] flex-shrink-0">
                                  <img
                                    src={post.image}
                                    alt={post.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[10px] font-semibold text-[var(--leaf)] uppercase tracking-wide mb-0.5 truncate">{post.category}</p>
                                  <p className="text-sm text-[var(--ink)] leading-snug line-clamp-2 group-hover:text-[var(--leaf)] transition-colors duration-200">{post.title}</p>
                                </div>
                              </Link>
                            ))}
                          </div>

                          {/* Column 3 — latest post */}
                          {blogList[0] && (
                            <div className="bg-[var(--mist)] rounded-xl p-5 h-fit">
                              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)] mb-3">Latest</p>
                              <Link to={`/blog/${blogList[0].slug}`}
                                onClick={() => afterNavigateToPath(`/blog/${blogList[0].slug}`)}
                                className="block text-left group cursor-pointer w-full"
                              >
                                <div className="rounded-lg overflow-hidden mb-3 aspect-video bg-white">
                                  <img
                                    src={blogList[0].image}
                                    alt={blogList[0].title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  />
                                </div>
                                <p className="text-[11px] font-semibold text-[var(--leaf)] uppercase tracking-wide mb-1.5">{blogList[0].category}</p>
                                <p className="text-sm font-semibold text-[var(--ink)] leading-snug">{blogList[0].title}</p>
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              }

              return (
                <li key={item.key}>
                  <Link to={`/${item.key}`}
                    onClick={() => afterNavigate(item.key)}
                    className={`inline-block relative px-4 py-2 text-[15px] font-medium rounded-lg transition-all duration-300 cursor-pointer
                      ${selected === item.key
                        ? "text-[var(--leaf)]"
                        : "text-white/85 hover:text-[var(--leaf)]"
                      }`}
                  >
                    {item.label}
                    {selected === item.key && (
                      <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-[var(--leaf)] rounded-full" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          <button onClick={() => setIsOpen(true)} className="ea-button ea-primary">
            Contact Us
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          ref={burgerRef}
          type="button"
          aria-label="Open menu"
          aria-expanded={open}
          className="xl:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/15 transition-colors duration-200 cursor-pointer"
          onClick={handleOpen}
          onPointerDown={preloadDrawer}
        >
          <LuMenu className="text-lg text-white" />
        </button>
      </div>

      {/* Mobile Drawer — mounted on first open, then kept mounted so the
          close animation still plays. */}
      {drawerMounted && (
      <Suspense fallback={null}>
      <Drawer
        placement="right"
        width="75%"
        open={open}
        onClose={handleClose}
        rootClassName="nav-drawer"
        aria-label="Site navigation"
        afterOpenChange={(isOpen) => { if (!isOpen) burgerRef.current?.focus(); }}
        styles={{ body: { padding: "24px 20px", background: "var(--paper)" }, header: { display: "none" } }}
      >
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center mb-8">
            <img src={drawerLogo} alt="InQmise Technologies" className="h-8 w-auto" />
            <button type="button" aria-label="Close menu" onClick={handleClose} className="w-9 h-9 rounded-lg bg-[var(--mist)] flex items-center justify-center cursor-pointer">
              <LuX className="text-sm text-[var(--ink)]" />
            </button>
          </div>

          <ul className="list-none m-0 p-0 space-y-1 flex-1 overflow-y-auto">
            {navItems.map((item) => {
              if (item.key === "ai") {
                return (
                  <li key={item.key}>
                    <div
                      className={`flex items-center rounded-lg transition-all duration-200
                        ${isAiMenuActive ? "bg-[var(--mist)] text-[var(--leaf)]" : "text-[var(--ink)]"}`}
                    >
                      <button
                        onClick={() => toggleMobileSection("ai")}
                        aria-expanded={mobileServicesOpen}
                        className="flex-1 text-left px-4 py-3 text-base font-medium cursor-pointer"
                      >
                        {item.label}
                      </button>
                      <button
                        onClick={() => toggleMobileSection("ai")}
                        aria-label={mobileServicesOpen ? "Collapse AI Solutions" : "Expand AI Solutions"}
                        aria-expanded={mobileServicesOpen}
                        className="px-4 py-3 cursor-pointer"
                      >
                        <FaChevronDown className={`text-xs transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {mobileServicesOpen && (
                      <ul className="list-none m-0 pl-2 pt-1 pb-2 space-y-0.5">
                        {aiServicePages.map((s) => (
                          <li key={s.path}>
                            <Link to={s.path}
                              onClick={() => afterNavigateToPath(s.path)}
                              className={`inline-block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer
                                ${selected === s.path.split("/")[1]
                                  ? "bg-[var(--mist)] text-[var(--leaf)]"
                                  : "text-[var(--muted)] hover:bg-[var(--mist)]"
                                }`}
                            >
                              {s.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              if (item.key === "services") {
                return (
                  <li key={item.key}>
                    <div
                      className={`flex items-center rounded-lg transition-all duration-200
                        ${selected === "services" ? "bg-[var(--mist)] text-[var(--leaf)]" : "text-[var(--ink)]"}`}
                    >
                      <button
                        onClick={() => toggleMobileSection("services")}
                        aria-expanded={mobileCompanyServicesOpen}
                        className="flex-1 text-left px-4 py-3 text-base font-medium cursor-pointer"
                      >
                        {item.label}
                      </button>
                      <button
                        onClick={() => toggleMobileSection("services")}
                        aria-label={mobileCompanyServicesOpen ? "Collapse Services" : "Expand Services"}
                        aria-expanded={mobileCompanyServicesOpen}
                        className="px-4 py-3 cursor-pointer"
                      >
                        <FaChevronDown className={`text-xs transition-transform duration-300 ${mobileCompanyServicesOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {mobileCompanyServicesOpen && (
                      <div className="pl-2 pt-1 pb-2 space-y-1">
                        {serviceCategories.map((category) => {
                          const isCategoryOpen = mobileServiceCategory === category.title;
                          return (
                            <div key={category.title}>
                              <button
                                onClick={() => toggleMobileServiceCategory(category.title)}
                                aria-expanded={isCategoryOpen}
                                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg text-left cursor-pointer"
                              >
                                <span className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">{category.title}</span>
                                <FaChevronDown className={`text-[10px] text-[var(--muted)] flex-shrink-0 transition-transform duration-300 ${isCategoryOpen ? "rotate-180" : ""}`} />
                              </button>
                              {isCategoryOpen && (
                                <ul className="list-none m-0 space-y-0.5">
                                  {category.items.map((catItem) => {
                                    const hasFacets = Boolean(catItem.facets?.length);
                                    const isFacetOpen = hasFacets && mobileExpandedFacetItem === catItem.path;
                                    return (
                                      <li key={catItem.path}>
                                        <div className="flex items-center gap-1">
                                          <Link to={catItem.path}
                                            onClick={() => afterNavigateToPath(catItem.path)}
                                            className={`inline-block flex-1 text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer
                                              ${isFacetOpen ? "text-[var(--leaf)] bg-[var(--mist)]" : "text-[var(--muted)] hover:bg-[var(--mist)]"}`}
                                          >
                                            {catItem.title}
                                          </Link>
                                          {hasFacets && (
                                            <button
                                              onClick={() => toggleMobileFacetItem(catItem.path)}
                                              aria-expanded={isFacetOpen}
                                              aria-label={isFacetOpen ? `Collapse ${catItem.title} details` : `Expand ${catItem.title} details`}
                                              className={`flex-shrink-0 w-9 h-9 flex items-center justify-center text-base rounded-lg transition-colors duration-200 cursor-pointer
                                                ${isFacetOpen ? "text-[var(--leaf)] bg-[var(--mist)]" : "text-[var(--muted)] hover:bg-[var(--mist)] hover:text-[var(--leaf)]"}`}
                                            >
                                              {isFacetOpen ? "−" : "+"}
                                            </button>
                                          )}
                                        </div>
                                        {isFacetOpen && (
                                          <ul className="list-none m-0 pl-4 py-1 space-y-0.5">
                                            {catItem.facets.map((facet) => (
                                              <li key={facet.path}>
                                                <Link to={facet.path}
                                                  onClick={() => afterNavigateToPath(facet.path)}
                                                  className="inline-block w-full text-left px-4 py-2 rounded-lg text-sm text-[var(--muted)] hover:bg-[var(--mist)] hover:text-[var(--leaf)] transition-all duration-200 cursor-pointer"
                                                >
                                                  {facet.title}
                                                </Link>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </li>
                );
              }

              if (item.key === "product") {
                const mobileProductOpen = mobileOpenSection === "product";
                return (
                  <li key={item.key}>
                    <div
                      className={`flex items-center rounded-lg transition-all duration-200
                        ${productPages.some((product) => product.path === `/${selected}`) ? "bg-[var(--mist)] text-[var(--leaf)]" : "text-[var(--ink)]"}`}
                    >
                      <button
                        onClick={() => toggleMobileSection("product")}
                        aria-expanded={mobileProductOpen}
                        className="flex-1 text-left px-4 py-3 text-base font-medium cursor-pointer"
                      >
                        {item.label}
                      </button>
                      <button
                        onClick={() => toggleMobileSection("product")}
                        aria-label={mobileProductOpen ? "Collapse Products" : "Expand Products"}
                        aria-expanded={mobileProductOpen}
                        className="px-4 py-3 cursor-pointer"
                      >
                        <FaChevronDown className={`text-xs transition-transform duration-300 ${mobileProductOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {mobileProductOpen && (
                      <ul className="list-none m-0 pl-2 pt-1 pb-2 space-y-0.5">
                        {productPages.map((product) => (
                          <li key={product.path}>
                            <Link to={product.path}
                              onClick={() => afterNavigateToPath(product.path)}
                              className="inline-block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-[var(--muted)] hover:bg-[var(--mist)] transition-all duration-200 cursor-pointer"
                            >
                              {product.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              if (item.key === "about") {
                return (
                  <li key={item.key}>
                    <div
                      className={`flex items-center rounded-lg transition-all duration-200
                        ${selected === "about" ? "bg-[var(--mist)] text-[var(--leaf)]" : "text-[var(--ink)]"}`}
                    >
                      <button
                        onClick={() => toggleMobileSection("about")}
                        aria-expanded={mobileAboutOpen}
                        className="flex-1 text-left px-4 py-3 text-base font-medium cursor-pointer"
                      >
                        {item.label}
                      </button>
                      <button
                        onClick={() => toggleMobileSection("about")}
                        aria-label={mobileAboutOpen ? "Collapse About" : "Expand About"}
                        aria-expanded={mobileAboutOpen}
                        className="px-4 py-3 cursor-pointer"
                      >
                        <FaChevronDown className={`text-xs transition-transform duration-300 ${mobileAboutOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {mobileAboutOpen && (
                      <ul className="list-none m-0 pl-2 pt-1 pb-2 space-y-0.5">
                        {aboutMenuItems.map((aboutItem) => (
                          <li key={aboutItem.path}>
                            <Link to={aboutItem.path}
                              onClick={() => afterNavigateToPath(aboutItem.path)}
                              className="inline-block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-[var(--muted)] hover:bg-[var(--mist)] transition-all duration-200 cursor-pointer"
                            >
                              {aboutItem.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              if (item.key === "resources") {
                return (
                  <li key={item.key}>
                    <div
                      className={`flex items-center rounded-lg transition-all duration-200
                        ${selected === "resources" ? "bg-[var(--mist)] text-[var(--leaf)]" : "text-[var(--ink)]"}`}
                    >
                      <button
                        onClick={() => toggleMobileSection("resources")}
                        aria-expanded={mobileResourcesOpen}
                        className="flex-1 text-left px-4 py-3 text-base font-medium cursor-pointer"
                      >
                        {item.label}
                      </button>
                      <button
                        onClick={() => toggleMobileSection("resources")}
                        aria-label={mobileResourcesOpen ? "Collapse Resources" : "Expand Resources"}
                        aria-expanded={mobileResourcesOpen}
                        className="px-4 py-3 cursor-pointer"
                      >
                        <FaChevronDown className={`text-xs transition-transform duration-300 ${mobileResourcesOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {mobileResourcesOpen && (
                      <ul className="list-none m-0 pl-2 pt-1 pb-2 space-y-0.5">
                        {resourceMenuItems.map((resourceItem) => (
                          <li key={resourceItem.path}>
                            <Link to={resourceItem.path}
                              onClick={() => afterNavigateToPath(resourceItem.path)}
                              className="inline-block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-[var(--muted)] hover:bg-[var(--mist)] transition-all duration-200 cursor-pointer"
                            >
                              {resourceItem.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li key={item.key}>
                  <Link to={`/${item.key}`}
                    onClick={() => afterNavigate(item.key)}
                    className={`inline-block w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 cursor-pointer
                      ${selected === item.key
                        ? "bg-[var(--mist)] text-[var(--leaf)]"
                        : "text-[var(--ink)] hover:bg-[var(--mist)]"
                      }`}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="mt-6 rounded-xl p-5 bg-[var(--sun)] relative overflow-hidden flex-shrink-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[var(--ink)]/60 mb-2">InQmise</p>
            <h4 className="text-lg font-bold text-[var(--ink)] mb-1.5 leading-snug" style={{ fontFamily: "Syne, sans-serif" }}>
              Have a project in mind?
            </h4>
            <p className="text-xs text-[var(--ink)]/70 mb-3 leading-relaxed">
              Let's scope it together, no obligation.
            </p>
            <button onClick={() => { setIsOpen(true); handleClose(); }} className="ea-text-link">
              Start a Conversation ↗
            </button>
          </div>
        </div>
      </Drawer>
      </Suspense>
      )}
    </header>
  );
};

export default Navbar;
